import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chooseExtensions, hasCodeEngine } from "@/workspace/code-engine";
import { Check, FileUp, Laptop, Link2, ListPlus, Monitor, Puzzle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  matchVsCode,
  parseCodeProfile,
  parseExtensionList,
  PLUGIN_CATALOG,
  VSCODE_SOURCE_LABELS,
  type PluginId,
  type VsCodeInstall,
  type VsCodeMatch,
  type VsCodeSource,
} from "@ide/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/overlay";
import { Input, Spinner, Textarea } from "@/components/ui/primitives";
import type { ImportedExtension } from "@ide/shared/vscode-content";
import { errorMessage, get, post, put } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { PLUGIN_ICONS } from "@/plugins/registry";
import { pluginKeys } from "@/plugins/use-plugins";
import { desktop, navigateTo } from "./bridge";
import { useDesktopUi } from "./ui";

/**
 * Importar plugins do VS Code para a CONTA: o app lê o VS Code (e Cursor, VSCodium, Windsurf)
 * deste computador — ou o perfil exportado, ou a lista `code --list-extensions` — e instala os
 * equivalentes do Kompil. Como ficam na conta, valem em qualquer computador em que a pessoa entrar.
 */
type Mode = "computer" | "profile" | "list";
interface Picked {
  source: VsCodeSource;
  extensions: string[];
  theme: string | null;
}

const pluginName = (id: PluginId) => PLUGIN_CATALOG.find((p) => p.id === id)?.name ?? id;
const pluginIcon = (id: PluginId) =>
  PLUGIN_ICONS[PLUGIN_CATALOG.find((p) => p.id === id)?.icon ?? ""] ?? Puzzle;

export const importKeys = { last: ["plugins", "import"] as const };

/** Resumo do conteúdo lido das pastas de extensões (temas, snippets e o que depende do VS Code). */
export function contentSummary(list: ImportedExtension[]) {
  return {
    themes: list.flatMap((e) => e.themes.map((t) => t.label)),
    snippets: list.reduce((n, e) => n + e.snippets.reduce((m, s) => m + s.items.length, 0), 0),
    needsHost: list.filter((e) => e.kind === "needs-host"),
  };
}

function Preview({ match, content }: { match: VsCodeMatch; content?: ImportedExtension[] }) {
  const [showOthers, setShowOthers] = useState(false);
  const summary = content ? contentSummary(content) : null;
  const unmatched = summary ? summary.needsHost.map((e) => `${e.name} — ${e.note ?? ""}`) : match.unmatched;
  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1.5 text-[12px] font-medium text-fg">
          Vão para a sua conta · {match.plugins.length} {match.plugins.length === 1 ? "plugin" : "plugins"}
        </p>
        {match.plugins.length ? (
          <div className="flex flex-wrap gap-1.5">
            {match.plugins.map((id) => {
              const Icon = pluginIcon(id);
              return (
                <span
                  key={id}
                  className="flex items-center gap-1.5 rounded-md border border-border bg-bg-subtle px-2 py-1 text-[12px] text-fg"
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {pluginName(id)}
                </span>
              );
            })}
          </div>
        ) : (
          <p className="text-[12px] text-fg-subtle">Nenhuma extensão com equivalente no Kompil ainda.</p>
        )}
      </div>
      {summary && (summary.themes.length || summary.snippets) ? (
        <div>
          <p className="mb-1.5 text-[12px] font-medium text-fg">Trazidos das extensões</p>
          <p className="text-[12px] leading-relaxed text-fg-muted">
            {summary.themes.length ? (
              <>
                {summary.themes.length} {summary.themes.length === 1 ? "tema de cor" : "temas de cor"} (
                {summary.themes.slice(0, 4).join(", ")}
                {summary.themes.length > 4 ? "…" : ""})
              </>
            ) : null}
            {summary.themes.length && summary.snippets ? " · " : null}
            {summary.snippets ? `${summary.snippets} snippets` : null}
          </p>
        </div>
      ) : null}
      <p className="text-[12px] text-fg-muted">
        {match.builtin.length ? (
          <>
            <Check className="mr-1 inline h-3.5 w-3.5" />
            {match.builtin.length} {match.builtin.length === 1 ? "extensão já vem" : "extensões já vêm"} no
            Kompil (sem instalar nada).{" "}
          </>
        ) : null}
        {unmatched.length ? (
          <button
            className="underline underline-offset-4 hover:text-fg"
            onClick={() => setShowOthers((v) => !v)}
          >
            {unmatched.length} {summary ? "funcionam pelo VS Code" : "sem equivalente ainda"}
          </button>
        ) : null}
      </p>
      {showOthers ? (
        <ul className="max-h-32 overflow-y-auto rounded-md bg-bg-muted px-2 py-1.5 text-[11.5px] leading-relaxed text-fg-subtle">
          {unmatched.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      ) : null}
      {hasCodeEngine() ? (
        <div className="rounded-md border border-border bg-bg-subtle p-2.5 text-[12px] leading-relaxed text-fg-muted">
          <p className="font-medium text-fg">Todas as extensões, funcionando de verdade</p>
          <p className="mt-0.5">
            As suas extensões do VS Code funcionam no Kompil como no próprio VS Code — todas, nas mesmas
            versões.
          </p>
          <button
            type="button"
            onClick={() => chooseExtensions()}
            className="mt-1.5 font-medium text-fg underline underline-offset-4"
          >
            Escolher e trazer as extensões
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function ImportVsCodeDialog() {
  const open = useDesktopUi((s) => s.importVsCode);
  const set = useDesktopUi((s) => s.set);
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>(desktop ? "computer" : "profile");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [profileUrl, setProfileUrl] = useState("");
  const [profile, setProfile] = useState<Picked | null>(null);
  const [list, setList] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const installs = useQuery({
    queryKey: ["vscode", "detect"],
    queryFn: () => desktop!.vscode.detect(),
    enabled: open && Boolean(desktop),
    staleTime: 0,
  });
  // Primeira abertura: todos os editores encontrados marcados.
  const found = useMemo(() => installs.data ?? [], [installs.data]);
  const [primed, setPrimed] = useState(false);
  if (!primed && installs.data) {
    setPrimed(true);
    setSelected(new Set(installs.data.filter((i) => i.extensions.length).map((i) => i.source)));
    if (!installs.data.length) setMode("profile");
  }

  const picked: Picked | null = useMemo(() => {
    if (mode === "computer") {
      const chosen = found.filter((i) => selected.has(i.source));
      if (!chosen.length) return null;
      return {
        source: chosen[0]!.source,
        extensions: [...new Set(chosen.flatMap((i) => i.extensions))],
        theme: chosen.find((i) => i.theme)?.theme ?? null,
      };
    }
    if (mode === "profile") return profile;
    const extensions = parseExtensionList(list);
    return extensions.length ? { source: "list", extensions, theme: null } : null;
  }, [mode, found, selected, profile, list]);
  const match = useMemo(() => (picked ? matchVsCode(picked.extensions, picked.theme) : null), [picked]);
  // Temas e snippets: lidos das pastas das extensões deste computador (só no app).
  const sources = useMemo(
    () => found.filter((i) => selected.has(i.source)).map((i) => i.source),
    [found, selected],
  );
  const content = useQuery({
    queryKey: ["vscode", "content", sources],
    queryFn: async () => {
      const lists = await Promise.all(sources.map((src) => desktop!.vscode.readExtensions(src)));
      const byId = new Map<string, ImportedExtension>();
      for (const e of lists.flat()) if (!byId.has(e.id)) byId.set(e.id, e);
      return [...byId.values()];
    },
    enabled: open && mode === "computer" && Boolean(desktop?.vscode.readExtensions) && sources.length > 0,
    staleTime: 60_000,
  });

  const run = useMutation({
    mutationFn: async (p: Picked) => {
      const extensions = mode === "computer" ? (content.data ?? []) : [];
      // O tema que a pessoa usava no VS Code, se veio de uma extensão: ele mesmo, convertido.
      const usedTheme = p.theme
        ? extensions
            .flatMap((e) => e.themes.map((t) => ({ extensionId: e.id, label: t.label })))
            .find((t) => t.label === p.theme)
        : undefined;
      if (extensions.length) {
        await put("/me/vscode-extensions", {
          source: p.source,
          extensions,
          ...(usedTheme ? { activate: usedTheme } : {}),
        });
      }
      const r = await post<{ installed: PluginId[]; already: PluginId[] }>("/me/plugins/import", {
        source: p.source,
        extensions: p.extensions,
        // Tema convertido do VS Code vence: os temas do Kompil ficam como estavam.
        ...(p.theme && !usedTheme ? { theme: p.theme } : {}),
      });
      return { ...r, summary: contentSummary(extensions), usedTheme };
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["vscode-extensions"] });
      void qc.invalidateQueries({ queryKey: pluginKeys.all });
      void qc.invalidateQueries({ queryKey: importKeys.last });
      set({ importVsCode: false });
      const parts = [
        r.installed.length
          ? `${r.installed.length} ${r.installed.length === 1 ? "plugin" : "plugins"}`
          : null,
        r.summary.themes.length
          ? `${r.summary.themes.length} ${r.summary.themes.length === 1 ? "tema" : "temas"}`
          : null,
        r.summary.snippets ? `${r.summary.snippets} snippets` : null,
      ].filter(Boolean);
      toast.success(
        parts.length ? `Trazido para a sua conta: ${parts.join(", ")}.` : "Tudo já estava na sua conta.",
        {
          description: "Ficam na sua conta do Kompil: valem em qualquer computador em que você entrar.",
          action: { label: "Ver plugins", onClick: () => navigateTo("/plugins?category=instalados") },
        },
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const readProfileFile = async (file: File) => {
    try {
      const parsed = parseCodeProfile(await file.text());
      setProfile({ source: "profile", extensions: parsed.extensions, theme: parsed.theme });
    } catch {
      toast.error("Esse arquivo não é um perfil do VS Code (.code-profile).");
    }
  };
  const loadProfileUrl = async () => {
    setLoadingProfile(true);
    try {
      const parsed = await post<{ extensions: string[]; theme: string | null }>(
        "/me/plugins/import/profile",
        {
          url: profileUrl,
        },
      );
      setProfile({ source: "profile", extensions: parsed.extensions, theme: parsed.theme });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoadingProfile(false);
    }
  };

  const close = () => set({ importVsCode: false });
  const tabs: Array<[Mode, string, typeof Laptop]> = [
    ...(desktop ? ([["computer", "Neste computador", Laptop]] as Array<[Mode, string, typeof Laptop]>) : []),
    ["profile", "Perfil exportado", Link2],
    ["list", "Lista de extensões", ListPlus],
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? set({ importVsCode: true }) : close())}>
      <DialogContent
        wide
        title="Trazer seus plugins do VS Code"
        description="As extensões que você usa viram os plugins equivalentes do Kompil, na sua conta — sem instalar um por um."
      >
        {me?.user.isGuest ? (
          <div className="space-y-3 text-[13px] text-fg-muted">
            <p>Os plugins ficam na sua conta do Kompil (grátis). Entre para trazer os do VS Code.</p>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  set({ importVsCode: false, login: "Entre para trazer seus plugins do VS Code." })
                }
              >
                Entrar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-3 gap-1 rounded-lg border border-border p-0.5">
              {tabs.map(([id, label, Icon]) => (
                <button
                  key={id}
                  onClick={() => setMode(id)}
                  className={cn(
                    "flex h-8 items-center justify-center gap-1.5 rounded-md text-[12.5px] font-medium transition-colors",
                    mode === id ? "bg-bg-emphasis text-fg" : "text-fg-muted hover:text-fg",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>

            {mode === "computer" ? (
              installs.isLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : found.length ? (
                <div className="mb-4 grid gap-2 sm:grid-cols-2">
                  {found.map((i: VsCodeInstall) => {
                    const on = selected.has(i.source);
                    return (
                      <button
                        key={i.source}
                        onClick={() =>
                          setSelected((s) => {
                            const next = new Set(s);
                            if (next.has(i.source)) next.delete(i.source);
                            else next.add(i.source);
                            return next;
                          })
                        }
                        className={cn(
                          "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                          on ? "border-fg bg-bg-emphasis" : "border-border hover:border-border-strong",
                        )}
                        aria-pressed={on}
                      >
                        <Monitor className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-medium text-fg">
                            {VSCODE_SOURCE_LABELS[i.source]}
                          </span>
                          <span className="block truncate text-[11.5px] text-fg-subtle">
                            {i.extensions.length} {i.extensions.length === 1 ? "extensão" : "extensões"}
                            {i.theme ? ` · tema ${i.theme}` : ""}
                          </span>
                        </span>
                        <span
                          className={cn(
                            "flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border",
                            on ? "border-fg bg-fg text-fg-inverse" : "border-border-strong",
                          )}
                        >
                          {on ? <Check className="h-3 w-3" /> : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="mb-4 text-[12.5px] text-fg-muted">
                  Não achamos o VS Code neste computador. Use o perfil exportado ou a lista de extensões.
                </p>
              )
            ) : null}

            {mode === "profile" ? (
              <div className="mb-4 space-y-2 text-[12.5px] text-fg-muted">
                <p>
                  No VS Code: <strong className="text-fg">Perfis › Exportar perfil</strong> (com a conta do
                  GitHub) e cole o link — ou escolha o arquivo{" "}
                  <span className="font-mono">.code-profile</span>.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={profileUrl}
                    onChange={(e) => setProfileUrl(e.target.value)}
                    placeholder="https://vscode.dev/profile/github/…"
                    spellCheck={false}
                  />
                  <Button
                    variant="secondary"
                    loading={loadingProfile}
                    disabled={!profileUrl.trim()}
                    onClick={() => void loadProfileUrl()}
                  >
                    Ler
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => fileRef.current?.click()}
                    aria-label="Escolher arquivo .code-profile"
                  >
                    <FileUp className="h-3.5 w-3.5" />
                  </Button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".code-profile,application/json"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void readProfileFile(f);
                      e.target.value = "";
                    }}
                  />
                </div>
              </div>
            ) : null}

            {mode === "list" ? (
              <div className="mb-4 space-y-2 text-[12.5px] text-fg-muted">
                <p>
                  Em qualquer computador com VS Code, rode{" "}
                  <span className="rounded bg-bg-muted px-1 font-mono">code --list-extensions</span> e cole a
                  saída:
                </p>
                <Textarea
                  value={list}
                  onChange={(e) => setList(e.target.value)}
                  rows={5}
                  placeholder={"github.copilot\nms-python.python\nesbenp.prettier-vscode"}
                  className="font-mono text-[12px]"
                  spellCheck={false}
                />
              </div>
            ) : null}

            {match ? (
              <Preview match={match} content={mode === "computer" ? content.data : undefined} />
            ) : null}

            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                disabled={!picked || !match?.plugins.length}
                loading={run.isPending}
                onClick={() => picked && run.mutate(picked)}
              >
                Importar para a minha conta
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Primeira vez no app com conta: se o VS Code deste computador tem extensões com equivalente,
 * oferece trazer (uma vez por conta neste computador).
 */
export function VsCodeOffer() {
  const { data: me } = useMe();
  const userId = me && !me.user.isGuest ? me.user.id : null;
  useEffect(() => {
    if (!desktop || !userId) return;
    const key = `kompil.vscodeOffer.${userId}`;
    try {
      if (localStorage.getItem(key)) return;
    } catch {
      return;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      const [last, found] = await Promise.all([
        get<{ last: unknown }>("/me/plugins/import").catch(() => ({ last: true })),
        desktop!.vscode.detect().catch(() => [] as VsCodeInstall[]),
      ]);
      if (!alive || last.last) return;
      const all = found.flatMap((i) => i.extensions);
      const match = matchVsCode(all, found.find((i) => i.theme)?.theme ?? null);
      if (!match.plugins.length) return;
      try {
        localStorage.setItem(key, String(Date.now()));
      } catch {
        // sem armazenamento: pode oferecer de novo
      }
      const editor = VSCODE_SOURCE_LABELS[found[0]!.source];
      toast(`Encontramos o ${editor} com ${all.length} extensões`, {
        description: `${match.plugins.length} têm equivalente no Kompil. Trazer para a sua conta?`,
        duration: 20_000,
        action: { label: "Trazer", onClick: () => useDesktopUi.getState().set({ importVsCode: true }) },
      });
    }, 2_500);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [userId]);
  return null;
}
